import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";
import FileUploader from "@/components/FileUploader";

export default function StoriesBar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [liveArtistIds, setLiveArtistIds] = useState(new Set());
  const [liveArtistInfo, setLiveArtistInfo] = useState({}); // id -> {id,name,avatar,liveId}
  const [viewer, setViewer] = useState(null); // {group, index}
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/stories"); setGroups(data); }
    catch { /* silent */ }
  };
  const loadLives = async () => {
    try {
      const { data } = await api.get("/lives");
      setLiveArtistIds(new Set(data.map((l) => l.artist_id)));
      const info = {};
      for (const l of data) {
        info[l.artist_id] = { id: l.artist_id, name: l.artist_name, avatar: l.artist_avatar, liveId: l.id };
      }
      setLiveArtistInfo(info);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load(); loadLives();
    const id = setInterval(loadLives, 15000);
    return () => clearInterval(id);
  }, []);

  const isArtist = user?.role === "artist";

  // Merge live artists who have no story tile yet — they still appear with the red LIVE ring.
  const mergedGroups = (() => {
    const existing = new Set(groups.map((g) => g.artist_id));
    const synthetics = Object.values(liveArtistInfo)
      .filter((a) => !existing.has(a.id))
      .map((a) => ({ artist_id: a.id, artist_name: a.name, artist_avatar: a.avatar, items: [], _synthetic: true }));
    // Live first, then existing groups
    const liveExisting = groups.filter((g) => liveArtistIds.has(g.artist_id));
    const restExisting = groups.filter((g) => !liveArtistIds.has(g.artist_id));
    return [...synthetics, ...liveExisting, ...restExisting];
  })();

  return (
    <>
      <div className="card-flat p-3 sm:p-4 anim-in" data-testid="stories-bar">
        <div className="flex items-center gap-3 overflow-x-auto scroll-hide">
          {isArtist && (
            <button
              onClick={() => setAdding(true)}
              className="flex flex-col items-center gap-1 shrink-0"
              data-testid="add-story-btn"
            >
              <span className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full p-[3px] bg-gradient-to-br from-zinc-800 to-zinc-900 grid place-items-center">
                <span className="w-full h-full rounded-full bg-zinc-950 border border-white/10 grid place-items-center text-zinc-400">
                  <Plus className="w-5 h-5" />
                </span>
              </span>
              <span className="text-[11px] text-zinc-400 max-w-[80px] truncate">{t.stories.add}</span>
            </button>
          )}

          {mergedGroups.length === 0 && !isArtist && (
            <p className="text-zinc-500 text-sm py-4">{t.stories.empty}</p>
          )}

          {mergedGroups.map((g) => {
            const isLive = liveArtistIds.has(g.artist_id);
            const isSynthetic = g._synthetic;
            return (
            <button
              key={g.artist_id}
              onClick={() => {
                if (isLive) { navigate(`/lives?start=${liveArtistInfo[g.artist_id]?.liveId}`); return; }
                if (!isSynthetic) setViewer({ group: g, index: 0 });
              }}
              className="flex flex-col items-center gap-1 shrink-0 relative"
              data-testid={`story-${g.artist_id}`}
            >
              <span className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full p-[3px] ${isLive ? "bg-[#FF0050] shadow-[0_0_18px_rgba(255,0,80,0.6)]" : "story-ring"}`}>
                <span className="block w-full h-full rounded-full overflow-hidden bg-zinc-900 border-2 border-zinc-950">
                  {g.artist_avatar ? (
                    <img src={resolveImg(g.artist_avatar)} alt={g.artist_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="w-full h-full grid place-items-center font-black text-white bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]">
                      {(g.artist_name || "?").charAt(0)}
                    </span>
                  )}
                </span>
                {isLive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center bg-[#FF0050] text-white text-[8px] font-black px-1.5 py-px rounded-full tracking-widest border border-zinc-950" data-testid={`story-live-${g.artist_id}`}>
                    LIVE
                  </span>
                )}
              </span>
              <span className="text-[11px] text-white max-w-[80px] truncate">{g.artist_name}</span>
            </button>
            );
          })}
        </div>
      </div>

      {viewer && (
        <StoryViewer
          group={viewer.group}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onChange={(i) => setViewer({ ...viewer, index: i })}
        />
      )}

      {adding && (
        <NewStoryModal
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); load(); }}
          existingGroup={groups.find((g) => g.artist_id === user?.id)}
        />
      )}
    </>
  );
}

function StoryViewer({ group, index, onClose, onChange }) {
  const total = group.stories.length;
  const story = group.stories[index];
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / 5000);
      setProgress(p);
      if (p >= 1) {
        if (index + 1 < total) onChange(index + 1);
        else onClose();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") index + 1 < total ? onChange(index + 1) : onClose();
      if (e.key === "ArrowLeft" && index > 0) onChange(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, total, onClose, onChange]);

  const handleTap = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    if (x < r.width / 3) { if (index > 0) onChange(index - 1); }
    else { if (index + 1 < total) onChange(index + 1); else onClose(); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-2xl grid place-items-center p-2 sm:p-4" data-testid="story-viewer">
      <div className="relative w-full max-w-md h-[80vh] sm:h-[88vh] rounded-2xl overflow-hidden bg-zinc-950 border border-white/10">
        {/* Progress bars */}
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
          {group.stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white"
                style={{ width: i < index ? "100%" : i === index ? `${progress * 100}%` : "0%" }}
              />
            </div>
          ))}
        </div>
        {/* Header */}
        <div className="absolute top-5 left-3 right-3 z-10 flex items-center gap-2">
          <img src={resolveImg(group.artist_avatar)} alt="" className="w-8 h-8 rounded-full object-cover border border-white/20" />
          <span className="text-white text-sm font-semibold">{group.artist_name}</span>
          <button onClick={onClose} className="ml-auto p-2 rounded-full bg-black/40 text-white" data-testid="story-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div onClick={handleTap} className="w-full h-full cursor-pointer">
          <img src={resolveImg(story.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        </div>

        {story.caption && (
          <div className="absolute bottom-6 left-4 right-4 text-white text-sm sm:text-base text-center">
            {story.caption}
          </div>
        )}

        <button onClick={() => index > 0 && onChange(index - 1)} className="hidden sm:grid absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 text-white place-items-center" disabled={index === 0}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => index + 1 < total ? onChange(index + 1) : onClose()} className="hidden sm:grid absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 text-white place-items-center">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function NewStoryModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [image, setImage] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!image.trim()) { toast.error(tr("Could not publish")); return; }
    setBusy(true);
    try {
      await api.post("/stories", { image: image.trim(), caption: caption.trim() });
      toast.success(tr("Posted!"));
      onCreated();
    } catch { toast.error(tr("Could not publish")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="story-modal">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-2xl font-black font-display text-white mb-4">{t.stories.newStory}</h3>
        <form onSubmit={submit} className="space-y-3">
          <FileUploader
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            label={t.stories.uploadImage}
            dataTestid="story-image-upload"
            preview={image}
            onUploaded={setImage}
            onClear={() => setImage("")}
          />
          <input className="input-base" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t.stories.captionPh} data-testid="story-caption" />
          <button type="submit" disabled={busy || !image} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="story-publish-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.stories.publish}
          </button>
        </form>
      </div>
    </div>
  );
}
